import { useCallback, useEffect, useMemo, useState } from "react";
import AppSidebar, { SB_W, SB_W_COLLAPSED } from "../../../AppSidebar";
import "../../../AppSidebar.css";
import "../../../MasterViewV2.css";
import { rbacApi } from "../api/rbacApi";
import "../styles/RbacManagementPage.css";
import { usePermissions } from "../../../context/PermissionsContext";

export default function RbacManagementPage({ user, showToast }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sb-collapsed") === "true"
  );
  const sbW = sidebarCollapsed ? SB_W_COLLAPSED : SB_W;

  const [roles,   setRoles]   = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  // Current role being viewed
  const [selectedRole, setSelectedRole] = useState("");

  // Role permissions from DB: { module_key: { action_key: { allowed, updated_by, updated_at } } }
  const [savedPerms, setSavedPerms] = useState({});

  // Local draft: { module_key: { action_key: bool } }
  const [draft, setDraft] = useState({});

  const [saving, setSaving] = useState(false);

  const { reload: reloadMyPerms } = usePermissions();

  // ── Load roles + module catalogue on mount ────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [rolesData, modulesData] = await Promise.all([
          rbacApi.listRoles(),
          rbacApi.listModules(),
        ]);
        setRoles(Array.isArray(rolesData) ? rolesData : []);
        setModules(Array.isArray(modulesData) ? modulesData : []);
        const firstNonAdmin = (Array.isArray(rolesData) ? rolesData : []).find(
          (r) => r.toLowerCase() !== "admin"
        );
        if (firstNonAdmin) setSelectedRole(firstNonAdmin);
        else if (rolesData?.[0]) setSelectedRole(rolesData[0]);
      } catch (err) {
        setError(err?.message || "Failed to load RBAC data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Load permissions for selected role ───────────────────────────────────
  const loadRolePerms = useCallback(async (role) => {
    if (!role || role.toLowerCase() === "admin" || role.toLowerCase() === "super admin") {
      setSavedPerms({});
      setDraft({});
      return;
    }
    try {
      const data = await rbacApi.getRolePermissions(role);
      const p = data?.permissions || {};
      setSavedPerms(p);
      // Initialise draft from saved
      const d = {};
      modules.forEach((mod) => {
        d[mod.module_key] = {};
        (mod.actions || []).forEach((act) => {
          d[mod.module_key][act.action_key] = p[mod.module_key]?.[act.action_key]?.allowed === true;
        });
      });
      setDraft(d);
    } catch (err) {
      showToast?.(err?.message || "Failed to load role permissions", "error");
    }
  }, [modules, showToast]);

  useEffect(() => {
    if (modules.length > 0 && selectedRole) loadRolePerms(selectedRole);
  }, [selectedRole, modules, loadRolePerms]);

  const isDirty = useMemo(() => {
    if (["admin", "super admin"].includes(selectedRole.toLowerCase())) return false;
    for (const mod of modules) {
      for (const act of (mod.actions || [])) {
        const current = draft[mod.module_key]?.[act.action_key] ?? false;
        const saved   = savedPerms[mod.module_key]?.[act.action_key]?.allowed === true;
        if (current !== saved) return true;
      }
    }
    return false;
  }, [draft, savedPerms, modules, selectedRole]);

  const handleToggle = (moduleKey, actionKey) => {
    if (["admin", "super admin"].includes(selectedRole.toLowerCase())) return;
    setDraft((prev) => ({
      ...prev,
      [moduleKey]: {
        ...(prev[moduleKey] || {}),
        [actionKey]: !(prev[moduleKey]?.[actionKey] ?? false),
      },
    }));
  };

  const handleSave = async () => {
    if (!selectedRole || ["admin", "super admin"].includes(selectedRole.toLowerCase())) return;
    setSaving(true);
    try {
      const grants = [];
      for (const mod of modules) {
        for (const act of (mod.actions || [])) {
          grants.push({
            module_key: mod.module_key,
            action_key: act.action_key,
            allowed:    draft[mod.module_key]?.[act.action_key] ?? false,
          });
        }
      }
      await rbacApi.setRolePermissions(selectedRole, grants);
      showToast?.(`Permissions saved for ${selectedRole}`);
      await loadRolePerms(selectedRole);
      reloadMyPerms();
    } catch (err) {
      showToast?.(err?.message || "Failed to save permissions", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleResetDeny = () => {
    const d = {};
    modules.forEach((mod) => {
      d[mod.module_key] = {};
      (mod.actions || []).forEach((act) => { d[mod.module_key][act.action_key] = false; });
    });
    setDraft(d);
  };

  const isAdmin = selectedRole.toLowerCase() === "admin" || selectedRole.toLowerCase() === "super admin";

  // Collect all unique action keys in order
  const allActionKeys = useMemo(() => {
    const seen = new Set();
    const keys = [];
    modules.forEach((mod) => {
      (mod.actions || []).forEach((act) => {
        if (!seen.has(act.action_key)) { seen.add(act.action_key); keys.push(act.action_key); }
      });
    });
    return keys;
  }, [modules]);

  return (
    <div className="rbac-root" style={{ gridTemplateColumns: `${sbW} 1fr` }}>
      <AppSidebar
        user={user}
        collapsed={sidebarCollapsed}
        onCollapse={() => {
          const next = !sidebarCollapsed;
          setSidebarCollapsed(next);
          localStorage.setItem("sb-collapsed", String(next));
        }}
      />

      <main className="rbac-main">
        <div className="rbac-page-header">
          <p className="rbac-page-title">Settings</p>
          <h1 className="rbac-page-heading">Access Control</h1>
        </div>

        {loading && <div className="rbac-state">Loading…</div>}
        {error   && <div className="rbac-state rbac-state--error">{error}</div>}

        {!loading && !error && (
          <>
            {/* Role selector */}
            <div className="rbac-role-strip">
              <span className="rbac-role-strip-label">Role</span>
              {roles.map((role) => (
                <button
                  key={role}
                  type="button"
                  className={[
                    "rbac-role-btn",
                    role.toLowerCase() === "admin" ? "rbac-role-btn--admin" : "",
                    role === selectedRole ? "rbac-role-btn--active" : "",
                  ].filter(Boolean).join(" ")}
                  onClick={() => {
                    if (role.toLowerCase() === "admin") setSelectedRole(role);
                    else setSelectedRole(role);
                  }}
                >
                  {role}
                  {(role.toLowerCase() === "admin" || role.toLowerCase() === "super admin") && " ★"}
                </button>
              ))}
            </div>

            {isAdmin && (
              <div className="rbac-admin-notice">
                {selectedRole} has full access to all modules and actions — this cannot be changed.
              </div>
            )}

            {!isAdmin && (
              <div className="rbac-toolbar">
                <div className="rbac-toolbar-left">
                  {isDirty && (
                    <>
                      <span className="rbac-dirty-dot" />
                      <span className="rbac-dirty-label">Unsaved changes</span>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  className="rbac-btn rbac-btn--ghost"
                  onClick={handleResetDeny}
                  disabled={saving}
                >
                  Reset to deny-all
                </button>
                <button
                  type="button"
                  className="rbac-btn"
                  onClick={handleSave}
                  disabled={saving || !isDirty}
                >
                  {saving ? "Saving…" : "Save permissions"}
                </button>
              </div>
            )}

            {/* Permission matrix */}
            <div className="rbac-table-wrap">
              <table className="rbac-table">
                <thead>
                  <tr>
                    <th>Module</th>
                    {allActionKeys.map((k) => (
                      <th key={k}>{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modules.map((mod) => {
                    const moduleActionKeys = new Set((mod.actions || []).map((a) => a.action_key));
                    return (
                      <tr key={mod.module_key}>
                        <td className="rbac-module-cell">
                          <span className="rbac-module-name">{mod.module_name}</span>
                        </td>
                        {allActionKeys.map((actionKey) => {
                          if (!moduleActionKeys.has(actionKey)) {
                            return <td key={actionKey} className="rbac-toggle-cell"><span className="rbac-na">—</span></td>;
                          }
                          const checked = isAdmin || (draft[mod.module_key]?.[actionKey] ?? false);
                          return (
                            <td key={actionKey} className="rbac-toggle-cell">
                              <label className={`rbac-toggle${isAdmin ? " rbac-toggle--disabled" : ""}`}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={isAdmin || saving}
                                  onChange={() => handleToggle(mod.module_key, actionKey)}
                                />
                                <span className="rbac-toggle-track" />
                                <span className="rbac-toggle-thumb" />
                              </label>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
